import { useEffect, useState } from "react";
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Group,
  Loader,
  Modal,
  NumberInput,
  Paper,
  Select,
  Stack,
  Text,
  Textarea,
  Title,
  Tooltip,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { IconChevronLeft, IconRefresh } from "@tabler/icons-react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCreateDailyLog, useDailyLogs, type CreateDailyLogData } from "@/hooks/useDailyLogs";
import { useProjectCostCodeOptions } from "@/hooks/useProjectCostCodes";
import { VoiceInput } from "@/components/VoiceInput";
import { getAutoWeather } from "@/lib/weather";

export const Route = createFileRoute(
  "/projects/$projectId/daily-logs/new" as any
)({
  component: NewDailyLogPage,
});

const WEATHER_CONDITIONS = [
  "Clear",
  "Partly Cloudy",
  "Cloudy",
  "Light Rain",
  "Rain",
  "Heavy Rain",
  "Thunderstorm",
  "Snow",
  "Fog",
  "Windy",
];

function NewDailyLogPage() {
  const params = Route.useParams();
  const projectId = "projectId" in params ? params.projectId : "";
  const navigate = useNavigate();
  const createDailyLog = useCreateDailyLog(projectId);

  // Fetch existing daily logs to check for duplicates
  const { data: dailyLogsData } = useDailyLogs(projectId);

  // Fetch cost code options for this project
  const { data: costCodeData, isLoading: costCodesLoading } = useProjectCostCodeOptions(projectId);

  // Auto-weather state
  const [weatherLoading, setWeatherLoading] = useState(false);
  const [weatherAutoFilled, setWeatherAutoFilled] = useState(false);

  // Duplicate date modal state
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [existingLogId, setExistingLogId] = useState<string | null>(null);

  const form = useForm<{
    date: Date;
    temp: number;
    conditions: string;
    costCodeId: string;
    crewCount: number;
    workPerformed: string;
    issues: string;
  }>({
    initialValues: {
      date: new Date(),
      temp: 55, // More neutral default - roughly US annual average
      conditions: "Partly Cloudy", // Most common condition, less committal than "Clear"
      costCodeId: "",
      crewCount: 1,
      workPerformed: "",
      issues: "",
    },
    validate: {
      temp: (value) =>
        value < -50 || value > 150 ? "Temperature must be between -50 and 150°F" : null,
      crewCount: (value) =>
        value < 0 ? "Crew count must be at least 0" : null,
      workPerformed: (value) =>
        !value || value.trim().length === 0 ? "Work performed is required" : null,
    },
  });

  // Fetch auto-weather on component mount
  const fetchAutoWeather = async () => {
    setWeatherLoading(true);
    try {
      const weather = await getAutoWeather();
      if (weather) {
        form.setFieldValue("temp", weather.temperature);
        form.setFieldValue("conditions", weather.conditions);
        setWeatherAutoFilled(true);
      }
    } catch (error) {
      console.warn("Failed to fetch auto-weather:", error);
    } finally {
      setWeatherLoading(false);
    }
  };

  // Auto-fetch weather on mount
  useEffect(() => {
    fetchAutoWeather();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset auto-filled flag when user manually changes weather
  const handleWeatherChange = () => {
    if (weatherAutoFilled) {
      setWeatherAutoFilled(false);
    }
  };

  const handleSubmit = form.onSubmit(async (values) => {
    try {
      const data: CreateDailyLogData = {
        date: values.date.toISOString().split("T")[0] || "", // Format as YYYY-MM-DD
        costCodeId: values.costCodeId || undefined,
        weather: {
          temp: values.temp,
          conditions: values.conditions,
        },
        crewCount: values.crewCount,
        workPerformed: values.workPerformed,
        issues: values.issues.trim().length > 0 ? values.issues : undefined,
      };

      // Check if a log already exists for this date
      const existingLog = dailyLogsData?.dailyLogs?.find(
        (log) => log.date === data.date
      );

      if (existingLog) {
        // Show confirmation modal
        setExistingLogId(existingLog.id);
        setDuplicateModalOpen(true);
        return;
      }

      // No duplicate, proceed with creation
      await createDailyLog.mutateAsync(data);

      notifications.show({
        title: "Success",
        message: "Daily log created successfully!",
        color: "green",
      });

      // Navigate back to the daily logs list
      navigate({
        to: "/projects/$projectId/daily-logs",
        params: { projectId },
      });
    } catch (error) {
      notifications.show({
        title: "Error",
        message: error instanceof Error ? error.message : "Failed to create daily log",
        color: "red",
      });
    }
  });

  const handleEditExisting = () => {
    if (existingLogId) {
      navigate({
        to: "/projects/$projectId/daily-logs/$logId",
        params: { projectId, logId: existingLogId },
      });
    }
  };

  const handleBack = () => {
    navigate({
      to: "/projects/$projectId/daily-logs",
      params: { projectId },
    });
  };

  return (
    <>
      {/* Duplicate Date Modal */}
      <Modal
        opened={duplicateModalOpen}
        onClose={() => setDuplicateModalOpen(false)}
        title="Daily Log Already Exists"
        centered
      >
        <Stack gap="md">
          <Text>
            A daily log already exists for this date. Would you like to edit the existing log instead?
          </Text>
          <Text size="sm" c="dimmed">
            Note: Only one daily log is allowed per date per project.
          </Text>
          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={() => setDuplicateModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="outline" onClick={handleEditExisting}>
              Edit Existing Log
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Paper p="xl" withBorder>
        <Stack gap="lg">
          <Group justify="space-between" align="center">
            <Button
              variant="subtle"
              leftSection={<IconChevronLeft size={16} />}
              onClick={handleBack}
            >
              Back to Daily Logs
            </Button>
          </Group>

          <Title order={2}>New Daily Log</Title>
          <Text c="dimmed" size="sm">
            Document today's work, crew, and field conditions
          </Text>

        <form onSubmit={handleSubmit}>
          <Stack gap="lg">
            {/* Date */}
            <DateInput
              label="Date"
              placeholder="Select date"
              {...form.getInputProps("date")}
              required
              size="md"
              maxDate={new Date()}
            />

            {/* Weather Section */}
            <Paper p="md" withBorder>
              <Stack gap="md">
                <Group justify="space-between" align="center">
                  <Group gap="xs">
                    <Text fw={500} size="sm">
                      Weather Conditions
                    </Text>
                    {weatherLoading && <Loader size="xs" />}
                    {weatherAutoFilled && !weatherLoading && (
                      <Badge size="xs" variant="light" color="green">
                        Auto-filled
                      </Badge>
                    )}
                  </Group>
                  <Tooltip label="Refresh weather from GPS">
                    <ActionIcon
                      variant="subtle"
                      size="sm"
                      onClick={fetchAutoWeather}
                      disabled={weatherLoading}
                    >
                      <IconRefresh size={16} />
                    </ActionIcon>
                  </Tooltip>
                </Group>

                <Group grow>
                  <NumberInput
                    label="Temperature"
                    placeholder="70"
                    {...form.getInputProps("temp")}
                    onChange={(value) => {
                      form.setFieldValue("temp", value as number);
                      handleWeatherChange();
                    }}
                    required
                    suffix="°F"
                    size="md"
                    min={-50}
                    max={150}
                  />

                  <Select
                    label="Conditions"
                    placeholder="Select weather"
                    {...form.getInputProps("conditions")}
                    onChange={(value) => {
                      form.setFieldValue("conditions", value || "Clear");
                      handleWeatherChange();
                    }}
                    data={WEATHER_CONDITIONS}
                    required
                    size="md"
                    searchable
                  />
                </Group>
              </Stack>
            </Paper>

            {/* Primary Activity (Cost Code) - Optional */}
            <Select
              label="Primary Activity (Optional)"
              placeholder={
                costCodesLoading
                  ? "Loading cost codes..."
                  : (costCodeData?.options?.length ?? 0) === 0
                    ? "No cost codes assigned to this project"
                    : "Select the main work activity"
              }
              description={
                (costCodeData?.options?.length ?? 0) === 0 && !costCodesLoading
                  ? "To track work by activity, assign cost codes to this project in Budget or Settings"
                  : "Tag this log with the main work activity's cost code"
              }
              {...form.getInputProps("costCodeId")}
              data={costCodeData?.options || []}
              searchable
              clearable
              disabled={costCodesLoading || (costCodeData?.options?.length ?? 0) === 0}
              size="sm"
              styles={{
                label: {
                  fontSize: "0.875rem",
                  fontWeight: 400,
                  color: "var(--mantine-color-dimmed)",
                },
                description: {
                  fontSize: "0.75rem",
                },
              }}
            />

            {/* Crew Count */}
            <NumberInput
              label="Crew Count"
              placeholder="Number of workers on site"
              {...form.getInputProps("crewCount")}
              required
              size="md"
              min={0}
              description="Total number of workers on site today"
            />

            {/* Work Performed */}
            <Box>
              <Group justify="space-between" align="flex-end" mb={4}>
                <Text size="sm" fw={500}>
                  Work Performed <span style={{ color: "var(--mantine-color-red-6)" }}>*</span>
                </Text>
                <VoiceInput
                  onTranscript={(text) => {
                    const current = form.values.workPerformed;
                    form.setFieldValue(
                      "workPerformed",
                      current ? `${current} ${text}` : text
                    );
                  }}
                  placeholder="Speak to add work performed..."
                />
              </Group>
              <Textarea
                placeholder="Describe the work completed today..."
                {...form.getInputProps("workPerformed")}
                size="md"
                minRows={6}
                autosize
                description="Be specific - what was accomplished, which areas, what materials were used. Use the microphone button to dictate."
              />
            </Box>

            {/* Issues (Optional) */}
            <Box>
              <Group justify="space-between" align="flex-end" mb={4}>
                <Text size="sm" fw={500}>
                  Issues & Notes (Optional)
                </Text>
                <VoiceInput
                  onTranscript={(text) => {
                    const current = form.values.issues;
                    form.setFieldValue(
                      "issues",
                      current ? `${current} ${text}` : text
                    );
                  }}
                  placeholder="Speak to add issues..."
                />
              </Group>
              <Textarea
                placeholder="Any problems, delays, or important notes..."
                {...form.getInputProps("issues")}
                size="md"
                minRows={4}
                autosize
                description="Document any delays, safety concerns, or items needing follow-up"
              />
            </Box>

            {/* Action Buttons */}
            <Group justify="flex-end" mt="md">
              <Button
                variant="subtle"
                onClick={handleBack}
                disabled={createDailyLog.isPending}
                size="md"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                loading={createDailyLog.isPending}
                size="md"
              >
                Save Daily Log
              </Button>
            </Group>
          </Stack>
        </form>
      </Stack>
    </Paper>
    </>
  );
}
